import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import api from "../lib/api";

let SESSION_TOKEN = Math.random().toString(36).slice(2);

/**
 * Address autocomplete using Google Places API (proxied through backend).
 * Props:
 *  - value, onChange (string) : controlled text input
 *  - onSelect(addr, details)  : called with { description, place_id } and resolved details {city, postal_code}
 *  - placeholder, className, testid, iconClassName
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Adresse",
  className = "",
  iconClassName = "left-4",
  testid,
}) {
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const fetchPredictions = (val) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.length < 3) {
      setPredictions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/places/autocomplete", {
          params: { input: val, session_token: SESSION_TOKEN },
        });
        setPredictions(data?.predictions || []);
        setOpen(true);
      } catch {
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const pickPrediction = async (p) => {
    onChange(p.description);
    setOpen(false);
    setPredictions([]);
    // fetch details (city, postal_code)
    try {
      const { data } = await api.get("/places/details", { params: { place_id: p.place_id } });
      if (onSelect) onSelect(p, data || {});
      // new session for next address
      SESSION_TOKEN = Math.random().toString(36).slice(2);
    } catch {
      if (onSelect) onSelect(p, {});
    }
  };

  const onKey = (e) => {
    if (!open || predictions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, predictions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pickPrediction(predictions[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={containerRef} className="relative">
      <MapPin className={`w-4 h-4 text-[#2BD391] absolute top-1/2 -translate-y-1/2 pointer-events-none z-10 ${iconClassName}`} />
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); fetchPredictions(e.target.value); setActiveIdx(-1); }}
        onFocus={() => { if (predictions.length > 0) setOpen(true); }}
        onKeyDown={onKey}
        className={className}
        data-testid={testid}
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="w-4 h-4 text-[#6B7280] absolute right-4 top-1/2 -translate-y-1/2 animate-spin" />
      )}
      {open && predictions.length > 0 && (
        <div className="absolute z-50 top-full mt-2 w-full bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl overflow-hidden">
          {predictions.map((p, i) => (
            <button
              type="button"
              key={p.place_id || i}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pickPrediction(p)}
              data-testid={`autocomplete-item-${i}`}
              className={`w-full text-left px-4 py-3 hover:bg-[#F7F9FB] border-b border-[#F0F2F5] last:border-0 transition-colors ${activeIdx === i ? "bg-[#E8FBF3]" : ""}`}
            >
              <div className="flex items-start gap-2.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#2BD391] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#2C3E50] truncate">{p.main_text || p.description}</div>
                  {p.secondary_text && <div className="text-xs text-[#6B7280] truncate">{p.secondary_text}</div>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
